// Bounded MCP copy. The backend stores CODES in mcp_servers.last_error and puts
// only enum reasons on the OAuth redirect (Issue 113); this module is the one
// place codes become operator-facing sentences. Never render a raw param.
export const LAST_ERROR_COPY = Object.freeze({
  auth_token_required:     'Token required — add it in Settings to finish connecting.',
  connect_failed:          'Could not connect. Check the URL, then try Refresh.',
  oauth_signin_required:   'Sign-in required — click Connect to sign in again.',
  oauth_access_denied:     'Sign-in was declined at the provider. Click Connect to try again.',
  oauth_exchange_failed:   'Sign-in did not complete. Click Connect to try again.',
  oauth_unsafe_endpoint:   'This server pointed sign-in at a private or non-HTTPS address, so Solomon stopped. It cannot be connected.',
  oauth_resource_mismatch: 'This server named a different resource than its own URL, so Solomon stopped. It cannot be connected.',
  no_pending_auth:         'That sign-in link expired or was already used. Click Connect to start again.',
  oauth_unavailable:       'OAuth sign-in is not configured on this deployment.',
  oauth_pending:           'Sign-in is half finished — finish or restart the connection first, then Refresh.',
  oauth_bulk_bless_refused: 'OAuth-connected servers stay gated — bless read-only tools one at a time after reviewing each.',
  oauth_bless_refused:      'This tool stays gated: on an OAuth-connected server only tools the server marks read-only can auto-run.',
  // Residual 7: the start route collapses every unrenderable errno to this one.
  upstream_unreachable:    'Could not reach that server — try again shortly.',
  // Residual 6: Connect found a session that only needed refreshing.
  already_connected:       'Already connected — no sign-in needed.',
});

/**
 * Codes map to copy; legacy prose rows (pre-Issue-113) pass through unchanged.
 * hasOwn, not a bare lookup: last_error is server-supplied, and `toString` or
 * `constructor` would otherwise resolve through the prototype and render a
 * function body at the operator (residual 5).
 */
export function lastErrorCopy(code) {
  if (!code) return '';
  return Object.hasOwn(LAST_ERROR_COPY, code) ? LAST_ERROR_COPY[code] : String(code);
}

const NOTICE_ERROR_COPY = Object.freeze({
  oauth_access_denied:     'Sign-in was declined at the provider. Open Settings › MCP Servers to try again.',
  oauth_exchange_failed:   'MCP sign-in did not complete. Open Settings › MCP Servers to try again.',
  oauth_unsafe_endpoint:   'MCP sign-in stopped: the server pointed at a private or non-HTTPS address.',
  oauth_resource_mismatch: 'MCP sign-in stopped: the server named a different resource than its own URL.',
  no_pending_auth:         'That MCP sign-in link expired or was already used. Start again from Settings › MCP Servers.',
  oauth_unavailable:       'OAuth sign-in is not configured on this deployment.',
  bad_query:               'MCP sign-in returned something Solomon refused to read. Start again from Settings › MCP Servers.',
});

/** @returns {{kind:'success'|'error', text:string} | null} */
export function mcpOAuthNotice({ mcp, reason } = {}) {
  if (mcp === 'connected') {
    return { kind: 'success', text: 'MCP server signed in. Its tools are gated until you bless them in Settings › MCP Servers.' };
  }
  if (mcp === 'error') {
    return { kind: 'error', text: NOTICE_ERROR_COPY[reason] || NOTICE_ERROR_COPY.oauth_exchange_failed };
  }
  return null;
}

// ── Loopback-assisted connect (Robinhood-shaped AS: loopback redirects only) ──
// The backend answers oauth/start { loopback:true } with a `relay` contract:
// { port, path, forwardTo }. This turns it into the ONE command the operator
// runs on their own machine. `forwardTo` must be an https URL (or http on
// localhost for a dev backend); anything else yields null and the UI refuses
// to render a command it cannot vouch for.
export const LOOPBACK_RELAY_SCRIPT = 'backend/scripts/mcp-oauth-loopback-relay.mjs';
export const LOOPBACK_DEFAULTS = Object.freeze({ port: 8787, path: '/callback' });

export function loopbackRelayCommand(relay) {
  if (!relay || typeof relay.forwardTo !== 'string') return null;
  let to;
  try { to = new URL(relay.forwardTo); } catch { return null; }
  const loopbackHost = to.hostname === 'localhost' || to.hostname === '127.0.0.1';
  if (to.protocol !== 'https:' && !(to.protocol === 'http:' && loopbackHost)) return null;
  const port = Number(relay.port);
  const path = typeof relay.path === 'string' ? relay.path : LOOPBACK_DEFAULTS.path;
  if (!Number.isInteger(port) || port < 1 || port > 65535) return null;
  if (!path.startsWith('/') || /[\s?#"'`]/.test(path)) return null;
  let cmd = `node ${LOOPBACK_RELAY_SCRIPT} --to ${to.origin}`;
  if (port !== LOOPBACK_DEFAULTS.port) cmd += ` --port ${port}`;
  if (path !== LOOPBACK_DEFAULTS.path) cmd += ` --path ${path}`;
  return cmd;
}

export const LOOPBACK_COPY = Object.freeze({
  button:   'Connect via this computer',
  title:    'For providers that only accept a localhost redirect (e.g. Robinhood): run a one-shot relay on this computer, then sign in.',
  step1:    'In a terminal on this computer, from the Solomon repo, run:',
  step2:    'Leave it running, then press Continue. After you approve at the provider, the relay hands the sign-in back to Solomon and exits.',
  continue: 'Continue to sign-in',
  cancel:   'Cancel',
  copy:     'Copy',
  copied:   'Copied',
  badRelay: 'Solomon returned a relay target it could not vouch for, so the command was not shown. Try again.',
});
